/**
 * CheckboxField component
 */

// Library
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// CSS Style for this module
import styles from './checkboxField.module.scss';

/**
 * Interface for CheckboxField props
 */
interface CheckboxFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * The value of the checkbox input
	 */
	value: boolean | undefined;
	/**
	 * onChange function for keeping track field value
	 */
	onChange?: (checked: boolean) => void;
	/**
	 * Whether the component should detect changes from outside and update the value of the checkbox input
	 */
	canDetectOutsideChange?: boolean;
	/**
	 * label text
	 */
	label?: string;
	/**
	 * Whether it lets interact with the checkbox input through the label or not
	 */
	isLabelClickable?: boolean;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Label content instead of label text
	 */
	children?: React.ReactNode;
}

/**
 * Checkbox field
 * @param {CheckboxFieldProps} Prop
 * @returns an styled checkbox input element
 */
export const CheckboxField = ({
	customStyles,
	value,
	label,
	onChange,
	canDetectOutsideChange = false,
	isLabelClickable = false,
	disabled = false,
	children,
}: CheckboxFieldProps) => {
	const [id] = useState(uuidv4());
	const [isChecked, setIsChecked] = useState(value);

	const handleCheckboxChange = (checked: boolean) => {
		if (onChange) {
			onChange(checked);
		}
		setIsChecked(checked);
	};

	useEffect(() => {
		if (canDetectOutsideChange) {
			setIsChecked(value);
		}
	}, [canDetectOutsideChange, value]);

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<input
				type="checkbox"
				checked={isChecked}
				onChange={e => handleCheckboxChange(e.target.checked)}
				disabled={disabled}
				className={styles.field}
				id={id}
			/>
			{(children || label) && (
				<label
					className={[
						styles.label,
						styles[`label-disabled-${disabled}`],
						styles[`label-clickable-${isLabelClickable}`],
					].join(' ')}
					htmlFor={isLabelClickable ? id : undefined}
				>
					{children ? children : label}
				</label>
			)}
		</div>
	);
};
